import { AgencyTypeId } from '@app/models';
import { ValidityPeriod, validityPeriodDetailed } from '@app/pcs/models';
import { Calendar, calendarDetailed, CalendarEntities, calendarSchema } from '@app/pcs/models/calendar';
import {
  pathElementsSchema,
  PathSection,
  pathSectionDetailed,
  PathSectionsEntities,
  pathSectionWithBaseTrainParameters,
  pathSectionWithCalendar,
  pathSectionWithTrainParameters
} from '@dossier/models/path-section';
import { ResponsibleRu, responsibleRu } from '@dossier/models/responsible-ru';
import { RuImPair } from '@dossier/models/ru-im-pair';
import { processStrategyForChild, processStrategyForParent } from '@dossier/models/schema-process-strategy';
import { gql } from 'apollo-angular';
import { schema } from 'normalizr';

interface SubPathPaPm {
  newSubPath: boolean;
}

export interface BaseSubPath {
  id?: number;
  persistedId?: number;
  warning?: string;
  sortOrder?: number;
  agencyTypeId?: AgencyTypeId;
  pathTitle?: string;
  tsiVariantId?: string;
  validityPeriod?: ValidityPeriod;
  territoryId?: number;
  paPmData?: SubPathPaPm;
  containsPap?: boolean;
}

export interface SubPathWithBlocks extends BaseSubPath {
  pathSectionBlocks: PathSection[][];
  calendar?: Calendar;
}

export interface SubPath extends BaseSubPath {
  pathElements: PathSection[];
  calendar?: Calendar;
  // TODO JKA: separate interface VariantSubPath
  responsiblePair?: RuImPair;
  responsibleRu?: ResponsibleRu;
}

export interface SubPathEntity extends BaseSubPath {
  territoryId: number;
  pathElements: number[];
}

export interface SubPathsEntities {
  [key: number]: SubPathEntity;
}

export interface NormalizedSubPath {
  entities: {
    subPaths: SubPathsEntities;
    pathElements: PathSectionsEntities;
    calendar?: CalendarEntities;
  };
  result: number;
}

export interface NormalizedSubPaths {
  entities: {
    subPaths: SubPathsEntities;
  };
  result: number[];
}

export const subPathsSchema = new schema.Entity('subPaths', {
  pathElements: [pathElementsSchema],
  calendar: calendarSchema
}, {
  idAttribute: 'id',
  processStrategy: processStrategyForChild('territory')
});

export const subPathSchema = new schema.Entity('subPaths', {
  pathElements: [pathElementsSchema],
  calendar: calendarSchema
}, {
  idAttribute: 'id',
  processStrategy: processStrategyForParent
});

export enum SubPathPropertyMap {
  PathTitle = 'title',
  ServiceDaysForReferencePoint = 'trafficPeriodText',
  TsiVariantId = 'tsiVariant'
}

export const subPathSchemaListSchema = new schema.Array(subPathSchema);

const subPathBase = gql`
    fragment subPathBase on SubPath {
        id
        persistedId
        sortOrder
        agencyTypeId {
            id
            name
        }
        serviceDaysForReferencePoint
        tsiVariantId
        pathTitle
        warning
        paPmData {
            newSubPath
        }
        containsPap
        responsibleRu {
          ...responsibleRu
        }
    }
    ${responsibleRu}
`;

export const subPathWithTrainParameters = gql`
    fragment subPathWithTrainParameters on SubPath {
        ...subPathBase
        validityPeriod {
            ...validityPeriodDetailed
        }
        pathElements {
            ...pathSectionWithTrainParameters
        }
        calendar {
            ...calendarDetailed
        }
    }
    ${subPathBase}
    ${validityPeriodDetailed}
    ${pathSectionWithTrainParameters}
    ${calendarDetailed}
`;

export const subPathWithoutCalendars = gql`
    fragment subPathWithoutCalendars on SubPath {
        ...subPathBase
        validityPeriod {
            ...validityPeriodDetailed
        }
        pathElements {
            ...pathSectionWithTrainParameters
        }
    }
    ${subPathBase}
    ${validityPeriodDetailed}
    ${pathSectionWithTrainParameters}
`;

export const subPathWithBaseTrainParameters = gql`
    fragment subPathWithBaseTrainParameters on SubPath {
        ...subPathBase
        validityPeriod {
            ...validityPeriodDetailed
        }
        pathElements {
            ...pathSectionWithBaseTrainParameters
        }
        calendar {
            ...calendarDetailed
        }
    }
    ${subPathBase}
    ${validityPeriodDetailed}
    ${pathSectionWithBaseTrainParameters}
    ${calendarDetailed}
`;

export const subPathForEdit = gql`
    fragment subPathForEdit on SubPath {
        ...subPathBase
        validityPeriod {
            ...validityPeriodDetailed
        }
        pathElements {
            ...pathSectionWithBaseTrainParameters
        }
    }
    ${subPathBase}
    ${validityPeriodDetailed}
    ${pathSectionWithBaseTrainParameters}
`;

export const subPathWithPathSectionCalendars = gql`
    fragment subPathWithPathSectionCalendars on SubPath {
        ...subPathBase
        validityPeriod {
            ...validityPeriodDetailed
        }
        pathElements {
            ...pathSectionWithCalendar
        }
    }
    ${subPathBase}
    ${validityPeriodDetailed}
    ${pathSectionWithCalendar}
`;

export const subPathDetailed = gql`
    fragment subPathDetailed on SubPath {
        ...subPathBase
        validityPeriod {
            ...validityPeriodDetailed
        }
        pathElements {
            ...pathSectionDetailed
        }
    }
    ${subPathBase}
    ${validityPeriodDetailed}
    ${pathSectionDetailed}
`;
